import clsx from 'clsx'
import { AnimatePresence, motion } from 'motion/react'
import { useEffect } from 'react'
import ButtonIcon from '../ui/buttons/ButtonIcon'
import IconClose from '../ui/icons/IconClose'
import { IconInfo } from '../ui/icons/IconInfo'
import TextDefault from '../ui/texts/TextDefault'

type CardInfoOverlayProps = {
    open: boolean
    onClose: () => void
}

const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.2, ease: 'easeOut' as const } },
    exit: { opacity: 0, transition: { duration: 0.15, ease: 'easeIn' as const } },
}

const cardVariants = {
    hidden: { opacity: 0, scale: 0.85, y: 20 },
    visible: {
        opacity: 1,
        scale: 1,
        y: 0,
        transition: { type: 'spring' as const, stiffness: 380, damping: 22, mass: 0.8 },
    },
    exit: { opacity: 0, scale: 0.9, transition: { duration: 0.15 } },
}

export default function CardInfoOverlay({ open, onClose }: CardInfoOverlayProps) {
    useEffect(() => {
        if (!open) return
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [open, onClose])

    return (
        <AnimatePresence>
            {open ? (
                <motion.div
                    data-ui-priority
                    className='z-50 absolute inset-0 flex justify-center items-center bg-black/55 backdrop-blur-sm pointer-events-auto px-4'
                    variants={overlayVariants}
                    initial='hidden'
                    animate='visible'
                    exit='exit'
                    role='dialog'
                    aria-modal='true'
                    aria-label='Infos zur Sammelkarte'
                    onClick={onClose}
                >
                    <div className='relative' onClick={e => e.stopPropagation()}>
                        <motion.div
                            className={clsx(
                                'relative flex flex-col items-center drop-shadow-2-black-stroke bg-white-stroke-even-darker border-black-stroke pattern-background',
                                'gap-3 px-6 pt-8 pb-8 border rounded-[32px] w-full max-w-80',
                            )}
                            variants={cardVariants}
                        >
                            {/* Header */}
                            <div className='flex items-center justify-center gap-1 w-full'>
                                <IconInfo
                                    width='24'
                                    height='24'
                                    className='shrink-0 text-white drop-shadow-[0_1px_0_#80a8c7]'
                                />
                                <TextDefault
                                    fontFamily='grandstander'
                                    fontWeight={900}
                                    italic
                                    tracking='0.02em'
                                    textColor='white'
                                // outlineColor='white'
                                // dropShadowColor='white'
                                >Infos zur Sammelkarte</TextDefault>
                            </div>

                            {/* Body */}
                            <p className='text-white font-nunito font-bold text-[16px] text-center leading-[22px] tracking-[-0.02em]'
                                style={{ textShadow: '0 1px 0 #06487C' }}
                            >
                                Die echte Sammelkarte gibt es nur in teilnehmenden Sparkassen.
                            </p>
                            <p className='text-white font-nunito font-bold text-[16px] text-center leading-[22px] tracking-[-0.02em]'
                                style={{ textShadow: '0 1px 0 #06487C' }}
                            >
                                Dafür müssen deine Eltern einen Termin vereinbaren.
                            </p>
                        </motion.div>

                        {/* Close button */}
                        <div className='left-1/2 absolute -translate-x-1/2 -bottom-6'>
                            <ButtonIcon
                                color='red'
                                variant='standalone'
                                popupCloseSound
                                icon={<IconClose width='100%' height='100%' />}
                                onClick={onClose}
                                aria-label='Schließen'
                            />
                        </div>
                    </div>
                </motion.div>
            ) : null}
        </AnimatePresence>
    )
}
